
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import NavBar from "./components/NavBar";
import ChatWidget from "./components/ChatWidget";
import { SupabaseStatus } from "./components/SupabaseStatus";
import Index from "./pages/Index";
import Shows from "./pages/Shows";
import Explore from "./pages/Explore";
import Guest from "./pages/Guest";
import Manager from "./pages/Manager";
import Settings from "./pages/Settings";
import Calendar from "./pages/Calendar";
import Docs from "./pages/Docs";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import OnboardingChat from "./pages/OnboardingChat";

const queryClient = new QueryClient();

// Protected route wrapper component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Main app layout with NavBar
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <div className="flex-1">
        {children}
      </div>
      <ChatWidget />
    </div>
  );
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <Routes>
      {/* Landing page is now the root route */}
      <Route path="/" element={<Landing />} />
      
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
      } />
      
      {/* Onboarding is now behind auth */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingChat />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            <Index />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/shows" element={
        <ProtectedRoute>
          <AppLayout>
            <Shows />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/explore" element={
        <ProtectedRoute>
          <AppLayout>
            <Explore />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/guest/:id" element={
        <ProtectedRoute>
          <AppLayout>
            <Guest />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/manager" element={
        <ProtectedRoute>
          <AppLayout>
            <Manager />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/calendar" element={
        <ProtectedRoute>
          <AppLayout>
            <Calendar />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/docs" element={
        <ProtectedRoute>
          <AppLayout>
            <Docs />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      {/* Catch-all route */}
      <Route path="*" element={
        <NotFound />
      } />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <SupabaseStatus />
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
